import React, { useState } from "react";
import type { RouterProps } from "react-router-dom";
import { Router, Routes, Route, useLocation } from "react-router-dom";
import { Layout } from "antd";

import type { User } from "@chirpstack/chirpstack-api-grpc-web/api/user_pb";

import Header from "./components/Header";
import Menu from "./components/Menu";
import FirstLoginPasswordModal from "./components/FirstLoginPasswordModal";

// dashboard
import Dashboard from "./views/dashboard/Dashboard";

// users
import Login from "./views/users/Login";
import ListUsers from "./views/users/ListUsers";
import CreateUser from "./views/users/CreateUser";
import EditUser from "./views/users/EditUser";
import ChangeUserPassword from "./views/users/ChangeUserPassword";

// tenants
import TenantRedirect from "./views/tenants/TenantRedirect";
import ListTenants from "./views/tenants/ListTenants";
import CreateTenant from "./views/tenants/CreateTenant";
import TenantLoader from "./views/tenants/TenantLoader";

// api keys
import ListAdminApiKeys from "./views/api-keys/ListAdminApiKeys";
import CreateAdminApiKey from "./views/api-keys/CreateAdminApiKey";

// regions
import ListRegions from "./views/regions/ListRegions";
import RegionDetails from "./views/regions/RegionDetails";

// device-profiles
import ListDeviceProfileVendors from "./views/device-profiles/ListVendors";
import VendorLoader from "./views/device-profiles/VendorLoader";
import CreateVendor from "./views/device-profiles/CreateVendor";

// stores
import SessionStore from "./stores/SessionStore";

import { GlobalDisplayDateTimeProvider } from "./contexts/DisplayDateTimeContext";

import history from "./history";

interface IProps extends Omit<RouterProps, "location" | "navigationType" | "navigator"> {
  history: typeof history;
  children: React.ReactNode;
}

const CustomRouter = ({ history, ...props }: IProps) => {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  React.useLayoutEffect(() => history.listen(setState), [history]);

  return <Router {...props} location={state.location} navigationType={state.action} navigator={history} />;
};

/** Must render under <Router> so useLocation matches hash history (#/login). */
function AppLayout({ user }: { user: User | undefined }) {
  const location = useLocation();
  const isLoginRoute = location.pathname === "/login" || location.pathname.startsWith("/login/");

  return (
    <Layout
      style={{
        minHeight: "100vh",
        // Let the login page background image show through (Ant Design default is gray).
        background: isLoginRoute ? "transparent" : undefined,
      }}
    >
      <Routes>
        <Route path="/" element={<TenantRedirect />} />
        <Route path="/login" element={<Login />} />
      </Routes>

      {user && (
        <GlobalDisplayDateTimeProvider>
          {/* First-login nudge — shown once per user (localStorage flag).
              Sits above the layout so it renders regardless of which
              route the user lands on after login. */}
          <FirstLoginPasswordModal user={user} />
          <div>
            <Layout.Header className="layout-header">
              <Header user={user} />
            </Layout.Header>
            <Layout className="layout">
              <Layout.Sider width="300" theme="light" className="layout-menu">
                <Menu />
              </Layout.Sider>
              <Layout.Content className="layout-content" style={{ padding: "24px 24px 24px" }}>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/tenants" element={<ListTenants />} />
                  <Route path="/tenants/create" element={<CreateTenant />} />
                  <Route path="/tenants/:tenantId/*" element={<TenantLoader />} />

                  <Route path="/users" element={<ListUsers />} />
                  <Route path="/users/create" element={<CreateUser />} />
                  <Route path="/users/:userId" element={<EditUser />} />
                  <Route path="/users/:userId/password" element={<ChangeUserPassword />} />

                  <Route path="/api-keys" element={<ListAdminApiKeys />} />
                  <Route path="/api-keys/create" element={<CreateAdminApiKey />} />

                  <Route path="/device-profiles/vendors" element={<ListDeviceProfileVendors />} />
                  <Route path="/device-profiles/vendors/create" element={<CreateVendor />} />
                  <Route path="/device-profiles/vendors/:vendorId/*" element={<VendorLoader />} />

                  <Route path="/regions" element={<ListRegions />} />
                  <Route path="/regions/:id" element={<RegionDetails />} />
                </Routes>
              </Layout.Content>
            </Layout>
          </div>
        </GlobalDisplayDateTimeProvider>
      )}
    </Layout>
  );
}

function App() {
  const [user, setUser] = useState<User | undefined>(SessionStore.getUser());
  SessionStore.on("change", () => {
    setUser(SessionStore.getUser());
  });

  return (
    <CustomRouter history={history}>
      <AppLayout user={user} />
    </CustomRouter>
  );
}

export default App;
